import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";
import { useCommunityLinks, platformIcon } from "@/hooks/useCommunityLinks";

// Official $ROBBED Links hub — single source of truth = Admin Link Manager (/community-links).
export default function OfficialLinks() {
  const { data: settings } = useSettings();
  const { data: links = [] } = useCommunityLinks();
  const tgLive = !!settings?.telegramLive;

  const clickLink = (l) => {
    api.post("/analytics/event", { name: "official_link_click", meta: { id: l.platform } }).catch(() => {});
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="official-links-page">
      <header className="text-center">
        <span className="tag inline-flex items-center gap-1.5">🔗 Official</span>
        <h1 className="headline text-cream text-4xl sm:text-5xl lg:text-6xl mt-4">
          OFFICIAL <span className="text-neon">$ROBBED</span> LINKS
        </h1>
        <p className="font-mono text-dim mt-3 text-sm sm:text-base">Everything $ROBBED. One place.</p>
      </header>

      <div className="mt-10 space-y-4" data-testid="official-links-list">
        {links.map((l) => {
          const Icon = platformIcon(l.platform);
          const cta = l.platform === "x" ? "Follow on X" : l.platform === "telegram" ? "Join Telegram" : "Open";
          return (
            <a key={l.id} href={l.url} target="_blank" rel="noreferrer" onClick={() => clickLink(l)}
              data-testid={`official-link-${l.platform}`}
              className="group comic-card p-5 sm:p-6 flex items-center gap-4 sm:gap-5 hover:border-neon transition-colors">
              <div className="shrink-0 w-14 h-14 grid place-items-center bg-ink3 border-3 border-black text-neon group-hover:border-neon transition-colors">
                <Icon className="w-7 h-7" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-900 uppercase text-cream text-lg leading-tight">{l.name}</div>
                {l.description && <p className="font-mono text-dim text-xs sm:text-sm mt-1">{l.description}</p>}
              </div>
              <span className="shrink-0 btn-neon !py-2 !px-3 !text-xs hidden sm:inline-flex">{cta} <ArrowUpRight className="w-4 h-4" /></span>
              <ArrowUpRight className="w-6 h-6 text-neon sm:hidden shrink-0" />
            </a>
          );
        })}

        {/* Telegram coming-soon placeholder — only when there's no live/enabled Telegram link */}
        {!tgLive && !links.some((l) => l.platform === "telegram") && (
          <div className="comic-card p-5 sm:p-6 flex items-center gap-4 opacity-70" data-testid="official-link-telegram-soon">
            <div className="shrink-0 w-14 h-14 grid place-items-center bg-ink3 border-3 border-black text-dim">
              {React.createElement(platformIcon("telegram"), { className: "w-7 h-7" })}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-900 uppercase text-cream text-lg leading-tight">Telegram <span className="text-gold">Coming Soon</span></div>
              <p className="font-mono text-dim text-xs sm:text-sm mt-1">The official ROBBED Telegram is on the way.</p>
            </div>
          </div>
        )}
      </div>

      <div className="comic-card p-4 sm:p-5 mt-8 flex items-start gap-3" data-testid="official-links-warning">
        <ShieldCheck className="w-5 h-5 text-neon shrink-0 mt-0.5" />
        <p className="font-mono text-[11px] sm:text-xs text-dim leading-relaxed">
          Only trust links listed here. $ROBBED is PRE-LAUNCH — there is no token, contract or mint yet.
          Anyone DMing you a "contract address" or asking for your seed phrase is a scammer.{" "}
          <Link to="/vault" className="text-neon hover:underline">See The Vault</Link> for full transparency.
        </p>
      </div>
    </div>
  );
}
